import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchClassProfileCalendar, cancelClassProfile } from '../../database/databaseClassProfile';
import { notifyWaitlistForClass } from '../../database/databaseNotifications';

export default function AdminProfileClasses() {
    const { clientId, clientName } = useParams();
    const [classes, setClasses] = useState([]);
    const [onlyFromToday, setOnlyFromToday] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            const { data } = await fetchClassProfileCalendar(clientId, false);
            console.log('Fetched class profile calendar for client:', data);
            setClasses(data || []);
        })();
    }, [clientId]);

    const formatDate = (iso) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
        } catch {
            return iso;
        }
    };

    const formatTime = (t) => {
        if (!t) return '—';
        if (/^\d{2}:\d{2}(:\d{2})?$/.test(t)) return t.slice(0, 5);
        const d = new Date(t);
        if (!isNaN(d.getTime())) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        return t;
    };

    const getDayOfWeek = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).getDay();
    };

    const todayStr = new Date().toISOString().slice(0, 10);

    const filteredClasses = onlyFromToday ? classes.filter((row) => row?.date && row.date >= todayStr) : classes;

    const findRecurring = (row) => {
        const rowDay = getDayOfWeek(row?.date);
        return classes.filter((r) => r.id !== row.id && r?.title === row?.title && formatTime(r?.starttime) === formatTime(row?.starttime) && getDayOfWeek(r?.date) === rowDay);
    };

    const handleCancelClick = (row) => {
        const recurring = findRecurring(row);
        setConfirmModal({ row, recurring });
    };

    const handleDeleteOne = async () => {
        const { row } = confirmModal;
        const record = await cancelClassProfile(row.id);
        if (record && !record.waiting_list) {
            notifyWaitlistForClass(record.calendar_class, row.title, `${row.date} ${formatTime(row.starttime)}`);
        }
        setClasses((prev) => prev.filter((r) => r.id !== row.id));
        setConfirmModal(null);
    };

    const goBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate('/admin/clients');
    };

    const handleDeleteAll = async () => {
        const { row, recurring } = confirmModal;
        const allToDelete = [row, ...recurring];

        setIsDeleting(true);
        setDeleteProgress({ current: 0, total: allToDelete.length });

        for (let i = 0; i < allToDelete.length; i++) {
            const r = allToDelete[i];
            const record = await cancelClassProfile(r.id);
            if (record && !record.waiting_list) {
                notifyWaitlistForClass(record.calendar_class, r.title, `${r.date} ${formatTime(r.starttime)}`);
            }
            setDeleteProgress({ current: i + 1, total: allToDelete.length });
        }

        const deletedIds = new Set(allToDelete.map((r) => r.id));
        setClasses((prev) => prev.filter((r) => !deletedIds.has(r.id)));
        setIsDeleting(false);
        setDeleteProgress({ current: 0, total: 0 });
        setConfirmModal(null);
    };

    const RowDesktop = ({ row }) => {
        const title = row?.title ?? '—';
        const date = formatDate(row?.date);
        const timeRange = `${formatTime(row?.starttime)} – ${formatTime(row?.endtime)}`;
        return (
            <tr className='hover:bg-sand/40'>
                <td className='border px-3 py-2 align-top break-words'>{title}</td>
                <td className='border px-3 py-2 align-top whitespace-nowrap'>{date}</td>
                <td className='border px-3 py-2 align-top whitespace-nowrap'>{timeRange}</td>
                <td className='border px-3 py-2 align-top text-center'>
                    <button onClick={() => handleCancelClick(row)} className='bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1 rounded'>
                        Cancelar
                    </button>
                </td>
            </tr>
        );
    };

    const CardMobile = ({ row }) => {
        const title = row?.title ?? '—';
        const date = formatDate(row?.date);
        const timeRange = `${formatTime(row?.starttime)} – ${formatTime(row?.endtime)}`;
        return (
            <div className='border border-sand rounded-lg p-4 shadow-sm bg-card'>
                <div className='flex flex-col gap-2'>
                    <div className='flex gap-2'>
                        <span className='w-[40%] text-sm font-medium text-ink/70'>Clase</span>
                        <span className='w-[60%] break-words'>{title}</span>
                    </div>
                    <div className='flex gap-2'>
                        <span className='w-[40%] text-sm font-medium text-ink/70'>Fecha</span>
                        <span className='w-[60%]'>{date}</span>
                    </div>
                    <div className='flex gap-2'>
                        <span className='w-[40%] text-sm font-medium text-ink/70'>Hora</span>
                        <span className='w-[60%]'>{timeRange}</span>
                    </div>
                    <button onClick={() => handleCancelClick(row)} className='mt-1 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-3 py-1.5 rounded self-start'>
                        Cancelar clase
                    </button>
                </div>
            </div>
        );
    };

    return (
        <section className='w-[95%] md:w-[90%] lg:w-[80%] mx-auto py-6'>
            {/* ── Blocking overlay while deleting all ── */}
            {isDeleting && (
                <div className='fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/60'>
                    <div className='bg-card rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-5 w-[90%] max-w-xs'>
                        {/* Spinner */}
                        <svg className='animate-spin text-red-600' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' width={48} height={48}>
                            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                            <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z' />
                        </svg>
                        <p className='text-ink font-semibold text-base text-center'>Cancelando clases...</p>
                        {/* Progress bar */}
                        <div className='w-full'>
                            <div className='flex justify-between text-xs text-ink/60 mb-1'>
                                <span>
                                    {deleteProgress.current} de {deleteProgress.total}
                                </span>
                                <span>{Math.round((deleteProgress.current / deleteProgress.total) * 100)}%</span>
                            </div>
                            <div className='w-full bg-sand rounded-full h-2.5'>
                                <div className='bg-red-600 h-2.5 rounded-full transition-all duration-300' style={{ width: `${(deleteProgress.current / deleteProgress.total) * 100}%` }} />
                            </div>
                        </div>
                        <p className='text-xs text-ink/50 text-center'>Por favor no cierres esta página</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
                <h2 className='text-xl sm:text-2xl font-serif text-terracotta'>Clases del Cliente: {clientName}</h2>
                <button onClick={goBack} className='w-[50%] sm:w-auto bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition'>
                    Volver
                </button>
                {/* Toggle: only from today */}
                <label className='flex items-center gap-2 cursor-pointer select-none text-sm text-ink/80'>
                    <div onClick={() => setOnlyFromToday((v) => !v)} className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${onlyFromToday ? 'bg-terracotta' : 'bg-sand'}`}>
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-cream rounded-full shadow transition-transform duration-200 ${onlyFromToday ? 'translate-x-5' : 'translate-x-0'}`} />
                    </div>
                    Solo clases desde hoy
                </label>
            </div>

            {/* Desktop Table */}
            <div className='hidden md:block overflow-x-auto rounded border border-sand'>
                <table className='w-full table-fixed border-collapse text-sm'>
                    <colgroup>
                        <col style={{ width: '44%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '26%' }} />
                        <col style={{ width: '12%' }} />
                    </colgroup>
                    <thead>
                        <tr className='bg-sand text-left text-terracotta-dark'>
                            <th className='border px-3 py-2'>Clase</th>
                            <th className='border px-3 py-2'>Fecha</th>
                            <th className='border px-3 py-2'>Hora</th>
                            <th className='border px-3 py-2 text-center'>Acción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredClasses.map((row) => (
                            <RowDesktop key={row.id} row={row} />
                        ))}
                        {filteredClasses.length === 0 && (
                            <tr>
                                <td colSpan={4} className='text-center py-4'>
                                    No hay registros.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Cards */}
            <div className='md:hidden space-y-3'>
                {filteredClasses.length === 0 && <div className='text-center py-4 border border-sand rounded'>No hay registros.</div>}
                {filteredClasses.map((row) => (
                    <CardMobile key={row.id} row={row} />
                ))}
            </div>

            {/* ── Confirmation Modal ── */}
            {confirmModal && !isDeleting && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
                    <div className='bg-card rounded-xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4'>
                        {confirmModal.recurring.length > 0 ? (
                            <>
                                <h3 className='text-lg font-serif text-terracotta'>Cancelar clase</h3>
                                <p className='text-sm text-ink/80'>
                                    Se encontraron <span className='font-semibold'>{confirmModal.recurring.length}</span> clase
                                    {confirmModal.recurring.length !== 1 ? 's' : ''} recurrente
                                    {confirmModal.recurring.length !== 1 ? 's' : ''} con el mismo título, día y horario. ¿Qué deseas hacer?
                                </p>
                                <div className='flex flex-col gap-2'>
                                    <button onClick={handleDeleteOne} className='w-full border border-red-600 text-red-600 hover:bg-red-50 font-semibold py-2 rounded-lg text-sm'>
                                        Cancelar solo esta clase
                                    </button>
                                    <button onClick={handleDeleteAll} className='w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 rounded-lg text-sm'>
                                        Cancelar todas ({confirmModal.recurring.length + 1})
                                    </button>
                                    <button onClick={() => setConfirmModal(null)} className='w-full text-ink/60 hover:text-ink text-sm py-1'>
                                        Volver
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 className='text-lg font-serif text-terracotta'>Cancelar clase</h3>
                                <p className='text-sm text-ink/80'>
                                    ¿Estás seguro que deseas cancelar la clase <span className='font-semibold'>"{confirmModal.row?.title}"</span> del {formatDate(confirmModal.row?.date)}?
                                </p>
                                <div className='flex flex-col gap-2'>
                                    <button onClick={handleDeleteOne} className='w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 rounded-lg text-sm'>
                                        Sí, cancelar clase
                                    </button>
                                    <button onClick={() => setConfirmModal(null)} className='w-full text-ink/60 hover:text-ink text-sm py-1'>
                                        Volver
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
